import React, { useState } from 'react';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import MapIcon from '@mui/icons-material/Map';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import NoteIcon from '@mui/icons-material/Note';
import { 
  Box, Typography, Button, Table, TableBody, 
  TableCell, TableHead, TableRow, TextField, MenuItem, IconButton, Collapse, Chip, Stack
} from '@mui/material';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';

// Helper para colores de estado (Data Visualization 101)
const getStatusColor = (status) => {
  const colors = {
    pendiente: '#ff9800',
    pagado: '#2196f3',
    enviado: '#9c27b0',
    entregado: '#4caf50',
    cancelado: '#f44336'
  };
  return colors[status] || '#757575';
};

export default function OrderRow({ order, updateOrderStatus, contactCustomer }) {
  const [open, setOpen] = useState(false);

  // Extraemos datos de envío (asumiendo la estructura que creamos en el paso anterior)
  const shipping = order.shippingInfo || {};
  const googleMapsUrl = shipping.lat ? `https://www.google.com/maps?q=${shipping.lat},${shipping.lng}` : null;

  return (
    <React.Fragment>
      <TableRow hover sx={{ '& > *': { borderBottom: 'unset' }, bgcolor: open ? '#f8f9fa' : 'inherit' }}>
        <TableCell>
          <IconButton size="small" onClick={() => setOpen(!open)}>
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        
        {/* ID Y FECHA: Data mínima necesaria para trazabilidad */}
        <TableCell>
          <Typography variant="caption" sx={{ fontFamily: 'monospace', fontWeight: 'bold', color: '#666' }}>
            #{order._id.slice(-6).toUpperCase()}
          </Typography>
          <Typography variant="body2" display="block">
            {new Date(order.createdAt).toLocaleDateString()}
          </Typography>
        </TableCell>

        {/* CLIENTE Y TELÉFONO */}
        <TableCell>
          <Typography variant="body2" fontWeight="bold">
            {shipping.nombre || `${order.user?.nombre} ${order.user?.apellido}`}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            {shipping.telefono || order.user?.username}
          </Typography>
        </TableCell>

        {/* TOTAL CON ÉNFASIS VISUAL */}
        <TableCell>
          <Typography variant="body2" sx={{ fontWeight: 900, color: '#2c3e50' }}>
            BOB {order.total?.toFixed(2)}
          </Typography>
        </TableCell>

        {/* SELECTOR DE ESTADO CON CHIP DE COLOR */}
        <TableCell>
          <TextField
            select
            size="small"
            value={order.estado}
            onChange={(e) => updateOrderStatus(order._id, e.target.value)}
            sx={{ 
              minWidth: 130,
              '& .MuiSelect-select': { 
                py: 0.5, 
                fontSize: '0.85rem', 
                fontWeight: 'bold',
                color: getStatusColor(order.estado)
              } 
            }}
          >
            {['pendiente', 'pagado', 'enviado', 'entregado', 'cancelado'].map((s) => (
              <MenuItem key={s} value={s} sx={{ fontSize: '0.85rem' }}>
                {s.toUpperCase()}
              </MenuItem>
            ))}
          </TextField>
        </TableCell>

        <TableCell align="right">
          <Button 
            variant="contained" 
            color="success" 
            size="small" 
            disableElevation
            startIcon={<WhatsAppIcon />}
            onClick={() => contactCustomer(shipping.telefono || order.user?.username, order._id)}
            sx={{ borderRadius: 2, textTransform: 'none' }}
          >
            Despachar
          </Button>
        </TableCell>
      </TableRow>

      {/* PANEL DETALLADO DE LOGÍSTICA */}
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 2, p: 3, bgcolor: '#fff', borderRadius: 2, border: '1px solid #e0e0e0', boxShadow: '0 2px 10px rgba(0,0,0,0.05)' }}>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={4}>
                
                {/* COLUMNA 1: LOGÍSTICA Y MAPA */}
                <Box sx={{ flex: 1 }}>
                  <Typography variant="overline" color="primary" fontWeight="bold">Hoja de Ruta / Delivery</Typography>
                  
                  <Box sx={{ mt: 2, display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                    <LocationOnIcon color="action" fontSize="small" />
                    <Box>
                      <Typography variant="body2" fontWeight="bold">Coordenadas de entrega:</Typography>
                      {googleMapsUrl ? (
                        <Button 
                          variant="outlined" 
                          size="small" 
                          startIcon={<MapIcon />}
                          onClick={() => window.open(googleMapsUrl, '_blank')}
                          sx={{ mt: 1, textTransform: 'none' }}
                        >
                          Abrir en Google Maps
                        </Button>
                      ) : (
                        <Typography variant="caption" color="error">No se proporcionó ubicación GPS</Typography>
                      )}
                    </Box>
                  </Box>

                  <Box sx={{ mt: 3, display: 'flex', alignItems: 'flex-start', gap: 1, bgcolor: '#fffde7', p: 1.5, borderRadius: 1 }}>
                    <NoteIcon color="action" fontSize="small" />
                    <Box>
                      <Typography variant="body2" fontWeight="bold">Instrucciones Especiales:</Typography>
                      <Typography variant="body2" sx={{ fontStyle: 'italic', color: '#555' }}>
                        {shipping.instrucciones || "Sin instrucciones adicionales."}
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                {/* COLUMNA 2: DETALLE DE PRODUCTOS */}
                <Box sx={{ flex: 1.5 }}>
                  <Typography variant="overline" color="primary" fontWeight="bold">Resumen del Pedido</Typography>
                  <Table size="small" sx={{ mt: 1 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold' }}>Item</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 'bold' }}>Cant.</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>Subtotal</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {order.items.map((item) => (
                        <TableRow key={item.product}>
                          <TableCell sx={{ fontSize: '0.85rem' }}>{item.nombre}</TableCell>
                          <TableCell align="center">x{item.cantidad}</TableCell>
                          <TableCell align="right">BOB {(item.cantidad * item.precio).toFixed(2)}</TableCell>
                        </TableRow>
                      ))}
                      <TableRow>
                        <TableCell colSpan={2} align="right" sx={{ fontWeight: 'bold', border: 'none' }}>TOTAL:</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold', border: 'none', color: 'primary.main' }}>
                          BOB {order.total?.toFixed(2)}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </Box>

              </Stack>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </React.Fragment>
  );
}